/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { Image, View } from 'react-native';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import ButtonView from '@airasia-phoenix/react-native/components/ButtonView';
import { ButtonViewType } from '@airasia-phoenix/react-native/components/ButtonView/types';
import { pd } from '@airasia-phoenix/react-native/tokens';
import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import styles from './styles';
import { defaultWebPushOptInCopy } from './constants';
import type { WebPushOptInProps } from './types';
import { useWebPushOptIn } from '../../hooks/useWebPushOptIn';

const ANNOUNCEMENT_ILLUSTRATION =
  process.env.NEXT_PUBLIC_ENV === 'prd'
    ? 'https://static.airasia.com/design/illustration/announcement.webp'
    : 'https://static.stgairasia.com/design/images/app/announcement.webp';

/**
 * Our own pre-prompt for browser push permission.
 *
 * Renders nothing until the MoEngage SDK has been injected by GTM, the browser
 * permission is still `default`, and any previous dismissal has expired — see
 * `useWebPushOptIn` for the full set of preconditions.
 */
const WebPushOptIn = ({
  copy,
  enabled,
  delayMs,
  snoozeDays,
  onError,
  onAllow,
  onDismiss,
}: WebPushOptInProps) => {
  const { visible, allow, dismiss } = useWebPushOptIn({
    enabled,
    delayMs,
    snoozeDays,
    onError,
  });
  const { title, body, allowLabel, dismissLabel } = {
    ...defaultWebPushOptInCopy,
    ...copy,
  };

  if (!visible || typeof document === 'undefined') return null;

  const handleAllow = () => {
    // `allow` hands straight to the native prompt and must stay inside this
    // gesture, so the analytics callback runs after it rather than before.
    allow();
    onAllow?.();
  };

  const handleDismiss = () => {
    dismiss();
    onDismiss?.();
  };

  return createPortal(
    <View pointerEvents="box-none" style={styles.wrapper}>
      <View style={styles.container}>
        <View style={styles.topContent}>
          <Image
            source={{ uri: ANNOUNCEMENT_ILLUSTRATION }}
            style={styles.illustration}
            resizeMode="contain"
          />
          <View style={styles.copy}>
            <TextView
              text={title}
              style={[
                getTextStyle(
                  pd.sys.typescale.body_text_medium,
                  pd.sys.colour.content_primary,
                ),
                styles.title,
              ]}
            />
            <TextView
              text={body}
              style={[
                getTextStyle(
                  pd.sys.typescale.sub_body_text,
                  pd.sys.colour.content_primary,
                ),
                styles.body,
              ]}
            />
          </View>
        </View>
        <View style={styles.actions}>
          <View style={styles.actionButton}>
            <ButtonView
              type={ButtonViewType.SECONDARY_SMALL}
              text={dismissLabel}
              onPress={handleDismiss}
              disabled={false}
              width="100%"
            />
          </View>
          <View style={styles.actionButton}>
            <ButtonView
              type={ButtonViewType.PRIMARY_SMALL}
              text={allowLabel}
              onPress={handleAllow}
              disabled={false}
              width="100%"
            />
          </View>
        </View>
      </View>
    </View>,
    document.body,
  );
};

export default WebPushOptIn;
